import React from 'react';
import styled from 'styled-components';
import {Card, Grid} from '@material-ui/core';
import {AccentColorOptions, useIsTwinmotionTheme} from 'epic-ue-theme';
import {
    Button,
    CheckIcon,
    Eyebrow,
    EyebrowProps,
    Hyperlink,
    RemoveIcon,
    TextLink,
    TooltipWrap,
    Typography,
    VerticalSpacer
} from '../../elements';
import {TextLinkUnderlineWrap} from '../links/TextLinkUnderlineWrap';

export const LicenseCardWrapper = styled.div<{index: number}>`
    && {
        border-radius: 0;
        box-shadow: none;
        border: 0px;
        position: relative;
    }

    &&.width-contained {
        max-width: 416px;
    }

    .bottom-links {
        padding-left: 12px;

        a.ue-link-wrap {
            text-decoration: underline;
            text-underline-position: under;
        }
    }

    &&.twinmotion {
        .license-card-top,
        .license-card-mid,
        .license-card-bottom {
            box-shadow: initial;
            border-radius: 0;

            //odd number license cards are white
            ${(p) => (p.index % 2 ? `background-color: ${p.theme.palette.common.white};` : '')}
        }
    }
`;

const LicenseCardTopWrapper = styled(Card)<{$active?: boolean; $accentColor?: AccentColorOptions}>`
    && {
        .h2,
        .paragraph {
            padding: 0 ${(p) => p.theme.spacing(6)}px;
            ${(p) => p.theme.breakpoints.down('sm')} {
                padding: 0 ${(p) => p.theme.spacing(4)}px;
            }
        }
        ${(p) =>
            p.$active && p.$accentColor
                ? `border-top: 8px solid ${p.theme.palette.accent[p.$accentColor]}`
                : ''};
    }
`;

const LicenseCardMidWrapper = styled(Card)`
    && {
        padding: 0 ${(p) => p.theme.spacing(6)}px;
        ${(p) => p.theme.breakpoints.down('sm')} {
            padding: 0 ${(p) => p.theme.spacing(4)}px;
        }
        button {
            width: 100%;
            &.primary {
                background-color: ${(p) => p.theme.palette.common.black};
                color: ${(p) => p.theme.palette.common.white};
            }
        }
        ${(p) => p.theme.breakpoints.up('md')} {
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
        }
    }
`;

const LicenseCardBottomWrapper = styled(Card)<{accentColor: AccentColorOptions}>`
    && {
        padding: 0 ${(p) => p.theme.spacing(6)}px;
        ${(p) => p.theme.breakpoints.down('sm')} {
            padding: 0 ${(p) => p.theme.spacing(4)}px;
        }
        svg {
            font-size: 1.8rem;
            margin-left: -${(p) => p.theme.spacing(0.5)}px;
        }
        .caption span {
            vertical-align: text-bottom;
        }
    }
`;

const AdditionalLink = styled.div<{accentColor?: AccentColorOptions}>`
    width: 100%;
    text-align: center;

    .license-card-link {
        display: contents;
    }
`;

const HyperlinkSeperator = styled.div<{accentColor?: AccentColorOptions}>`
    padding-right: 15px;
    padding-left: 15px;
`;

interface LicenseBulletsProps {
    accentColor?: AccentColorOptions;
    included?: boolean;
    bulletText?: string;
    tooltip?: string;
}

export interface LicenseCardProps {
    className?: string;
    accentColor?: AccentColorOptions;
    index?: number;
    key?: string;
    active?: boolean;
    title?: string;
    description?: string;
    descriptionIsCaption?: boolean;
    price?: string;
    hidePrice?: boolean;
    priceNote?: string;
    buttonText?: string;
    buttonHref?: string;
    bulletsEyebrow?: EyebrowProps;
    bullets?: LicenseBulletsProps[];
    bottomLinks?: {label?: string; href?: string}[];
    widthContained?: boolean;
    additionalLink?: {label?: string; href?: string; accentColor?: AccentColorOptions};
}

export const LicenseCard = ({
    className,
    accentColor = 'sky',
    index = 0,
    active,
    title,
    description,
    descriptionIsCaption = false,
    price = '$0',
    priceNote,
    hidePrice,
    buttonText = 'Buy Now',
    buttonHref,
    bulletsEyebrow = {text: ''},
    bullets,
    bottomLinks,
    widthContained,
    additionalLink
}: LicenseCardProps): JSX.Element => {
    const isTwinmotion = useIsTwinmotionTheme();

    return (
        <>
            <LicenseCardWrapper
                index={index}
                className={`license-card ${active ? 'active' : ''} ${
                    widthContained ? 'width-contained' : ''
                } ${isTwinmotion && 'twinmotion'}`}>
                <LicenseCardTopWrapper
                    className={`${className} license-card-top`}
                    $active={active}
                    $accentColor={accentColor}>
                    {title && (
                        <>
                            <VerticalSpacer
                                variant="custom"
                                spacing={7 - (active ? 1 : 0)}
                                downSmSpacing={5 - (active ? 1 : 0)}
                            />
                            <Typography variant="h2">{title}</Typography>
                        </>
                    )}
                    {description && (
                        <>
                            <VerticalSpacer variant="paragraph" />
                            <Typography
                                variant="p"
                                caption={descriptionIsCaption}
                                accentColor={accentColor}>
                                {description}
                            </Typography>
                        </>
                    )}
                </LicenseCardTopWrapper>
                <LicenseCardMidWrapper className={`${className} license-card-mid`}>
                    {!hidePrice && price && (
                        <>
                            <VerticalSpacer variant="custom" spacing={5} downSmSpacing={4} />
                            <Grid
                                container
                                direction="row"
                                justifyContent="flex-start"
                                alignItems="flex-end"
                                spacing={2}>
                                <Grid item xs={'auto'}>
                                    <Typography variant="h2" big color={accentColor} as="span">
                                        {price}
                                    </Typography>
                                </Grid>
                                <Grid item xs={true}>
                                    <Typography variant="p" caption color={accentColor}>
                                        {priceNote}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </>
                    )}
                    {buttonHref && (
                        <>
                            <VerticalSpacer variant="heading" />
                            <Button
                                type={active ? 'primary' : 'secondary'}
                                color={accentColor}
                                href={buttonHref}>
                                {buttonText}
                            </Button>
                        </>
                    )}
                </LicenseCardMidWrapper>
                <LicenseCardBottomWrapper
                    className={`${className} license-card-bottom`}
                    accentColor={accentColor}>
                    {bulletsEyebrow && bulletsEyebrow.text && (
                        <>
                            <VerticalSpacer variant="custom" spacing={7} downSmSpacing={5} />
                            <Eyebrow as="span" {...bulletsEyebrow} />
                        </>
                    )}
                    {bullets &&
                        bullets.length &&
                        bullets.map((bullet, i) => (
                            <React.Fragment key={`bullet-${bullet.bulletText}`}>
                                <VerticalSpacer variant="custom" spacing={3} downSmSpacing={2} />
                                <Grid
                                    container
                                    direction="row"
                                    justifyContent="flex-start"
                                    alignItems="flex-start"
                                    spacing={2}>
                                    <Grid item xs={'auto'}>
                                        {bullet.included ? (
                                            <CheckIcon color={accentColor} />
                                        ) : (
                                            <RemoveIcon />
                                        )}
                                    </Grid>
                                    <Grid item xs={true} style={{paddingLeft: '4px'}}>
                                        <TooltipWrap text={bullet.tooltip}>
                                            <Typography variant="p" accentColor={accentColor}>
                                                {bullet.bulletText}
                                            </Typography>
                                        </TooltipWrap>
                                    </Grid>
                                </Grid>
                            </React.Fragment>
                        ))}
                    {bottomLinks && (
                        <>
                            {' '}
                            <VerticalSpacer variant="heading" />
                            <Grid className="bottom-links" container direction="row" spacing={3}>
                                {bottomLinks?.map((link, i) => {
                                    return (
                                        <React.Fragment key={`link-${link.label}`}>
                                            {' '}
                                            {i > 0 && (
                                                <HyperlinkSeperator>|</HyperlinkSeperator>
                                            )}{' '}
                                            <TextLinkUnderlineWrap
                                                accentColor={accentColor}
                                                textColor="white">
                                                <TextLink href={link.href}>{link.label}</TextLink>{' '}
                                            </TextLinkUnderlineWrap>
                                        </React.Fragment>
                                    );
                                })}
                            </Grid>
                        </>
                    )}
                    <VerticalSpacer variant="custom" spacing={7} downSmSpacing={5} />
                </LicenseCardBottomWrapper>

                {additionalLink && (
                    <AdditionalLink className={`${widthContained ? 'width-contained' : ''}`}>
                        <VerticalSpacer variant="heading" />
                        <Hyperlink
                            href={additionalLink.href || '#'}
                            accentColor={additionalLink.accentColor || 'sky'}>
                            <Typography className="license-card-link" variant="p" caption={true}>
                                {additionalLink.label || 'Looking for a custom license?'}
                            </Typography>
                        </Hyperlink>
                    </AdditionalLink>
                )}
            </LicenseCardWrapper>
        </>
    );
};
